import gsap from 'gsap';
import WishAjax from "./wish-ajax";

export default class FiltersPostsAjax {
    constructor(options = {}) {
        this.grid = document.querySelector('.grid_posts');
        this.paginationContainer = document.querySelector('.pagination');
        this.filterForm = document.getElementById('filter-form');
        this.resetButton = document.getElementById('reset-filters');
        this.selectedList = document.getElementById('selected-list');
        this.searchInput = document.querySelector('#searchInput');
        this.loader = document.querySelector('.grid_loader');

        this.contentType = options.contentType || 'post';

        this.taxonomyMap = {
            'post': 'category',
            'product': 'product_cat',
            'incontournables': 'categories-incontournables'
        };

        if (!this.grid || !this.filterForm) {
            return;
        }

        this.init();
    }

    init() {
        this.setupEventListeners();
        const urlParams = new URLSearchParams(window.location.search);
        const categorySlug = urlParams.get('category');
        const parentSlug = urlParams.get('parent');

        let didAjax = false;

        if (categorySlug === 'all' && parentSlug) {
            const checkboxes = this.filterForm.querySelectorAll(`.category-checkbox[data-parent="${parentSlug}"]`);
            checkboxes.forEach(cb => cb.checked = true);

            const values = Array.from(checkboxes).map(cb => cb.value);
            this.updateSelectedFilters(values);
            this.fetchPosts();
            didAjax = true;
        }
        else if (categorySlug) {
            this.applyInitialFilter(categorySlug);
            didAjax = true;
        } else {
            this.fetchPosts();
            didAjax = true;
        }

        document.querySelectorAll(".category-checkbox:checked").forEach((checkbox) => {
            const accordionItem = checkbox.closest(".accordion-item");
            if (accordionItem) {
                const header = accordionItem.querySelector(".accordion-header");
                if (header && !header.classList.contains("active")) {
                    header.click();
                }
            }
        });

        if (!didAjax && this.grid && this.grid.children.length > 0 && !this.grid.classList.contains('has-animated')) {
            gsap.from(this.grid.children, {
                opacity: 0,
                duration: .5,
                stagger: .1,
                onComplete: () => {
                    this.grid.classList.add('has-animated');
                }
            });
        }
    }

    setupEventListeners() {
        this.debouncedApplyFilters = this.debounce(this.applyFilters.bind(this), 300);

        if (this.filterForm) {
            this.filterForm.addEventListener('change', (event) => {
                const target = event.target;

                if (target.dataset.parentCategory) {
                    const childCheckboxes = this.filterForm.querySelectorAll(`.category-checkbox[data-parent="${target.value}"]`);
                    childCheckboxes.forEach((checkbox) => {
                        checkbox.checked = target.checked;
                    });
                }

                this.debouncedApplyFilters();
            });
        }

        if (this.resetButton) {
            this.resetButton.addEventListener('click', () => this.resetFilters());
        }

        if (this.searchInput) {
            this.searchInput.addEventListener('input', () => this.debouncedApplyFilters());
        }
    }

    debounce(func, delay) {
        let timeout;
        return function (...args) {
            clearTimeout(timeout);
            timeout = setTimeout(() => func.apply(this, args), delay);
        };
    }

    applyFilters() {
        const selectedCategories = Array.from(
            this.filterForm.querySelectorAll('.category-checkbox:checked')
        ).map((checkbox) => checkbox.value);

        const searchTerm = this.searchInput ? this.searchInput.value.trim() : '';

        const params = new URLSearchParams();
        if (selectedCategories.length > 0) {
            params.set('category', selectedCategories.join(','));
        }
        if (searchTerm) {
            params.set('search', searchTerm);
        }
        const newUrl = `${window.location.pathname}?${params.toString()}`;
        window.history.pushState(null, '', newUrl);

        this.updateSelectedFilters(selectedCategories);
        this.fetchPosts(1);
    }

    resetFilters() {
        const checkboxes = this.filterForm.querySelectorAll('.category-checkbox');

        checkboxes.forEach((checkbox) => (checkbox.checked = false));

        if (this.searchInput) {
            this.searchInput.value = '';
        }

        this.updateSelectedFilters([]);
        this.applyFilters();
    }

    updateSelectedFilters(selectedCategories) {
        this.selectedList.innerHTML = '';

        if (selectedCategories.length === 0) {
            this.resetButton.style.display = 'none';
            this.selectedList.classList.remove('filtered');
            return;
        }

        this.selectedList.classList.add('filtered');

        const resetItem = document.createElement('div');
        resetItem.id = 'reset-filters';
        resetItem.classList.add('badge', 'badge-bordered');
        resetItem.style.cursor = 'pointer';

        const resetText = document.createElement('span');
        resetText.textContent = 'Tout déselectionner';
        resetItem.appendChild(resetText);

        const resetIcon = document.createElement('span');
        resetIcon.classList.add('remove-icon');
        resetItem.appendChild(resetIcon);
        resetItem.addEventListener('click', () => this.resetFilters());
        this.selectedList.appendChild(resetItem);

        selectedCategories.forEach((category) => {
            const checkbox = this.filterForm.querySelector(
                `.category-checkbox[value="${category}"]`
            );
            const categoryName = checkbox ? checkbox.dataset.categoryName : category;

            const listItem = document.createElement('div');
            listItem.dataset.category = category;
            listItem.style.cursor = 'pointer';
            listItem.classList.add('badge', 'badge-primary');

            const categoryText = document.createElement('span');
            categoryText.textContent = categoryName;
            listItem.appendChild(categoryText);

            const removeSpan = document.createElement('span');
            removeSpan.classList.add('remove-icon');
            listItem.appendChild(removeSpan);

            listItem.addEventListener('click', () => this.removeFilter(category));
            this.selectedList.appendChild(listItem);
        });

        this.resetButton.style.display = 'block';
    }

    removeFilter(category) {
        const checkbox = this.filterForm.querySelector(
            `.category-checkbox[value="${category}"]`
        );

        if (checkbox) {
            checkbox.checked = false;
        }

        this.applyFilters();
    }

    showLoader() {
        if( this.loader ) this.loader.style.display = 'block';
        if( this.grid ) this.grid.classList.add('loading');
    }

    hideLoader() {
        if( this.loader ) this.loader.style.display = 'none';
        if( this.grid ) this.grid.classList.remove('loading');
    }

    showError(message) {
        const errorContainer = document.querySelector('.error-message');

        if (!errorContainer) {
            const errorElement = document.createElement('div');
            errorElement.classList.add('error-message');
            errorElement.textContent = message;
            this.grid.parentNode.insertBefore(errorElement, this.grid);
            gsap.fromTo(
                errorElement,
                { opacity: 0, },
                { opacity: 1, duration: .3 }
            );
        } else {
            errorContainer.textContent = message;
        }

        setTimeout(() => {
            const errorElement = document.querySelector('.error-message');
            if( errorElement ) {
                gsap.to(errorElement, {
                    opacity: 0,
                    duration: .3,
                    onComplete: () => errorElement.remove(),
                });
            }
        }, 5000);
    }

    fetchPosts(page = 1) {
        this.showLoader();

        const selectedCategories = Array.from(
            this.filterForm.querySelectorAll('.category-checkbox:checked')
        ).map((checkbox) => checkbox.value);

        const searchTerm = this.searchInput ? this.searchInput.value.trim() : '';

        gsap.to(this.grid.children, {
            opacity: 0,
            duration: .3,
            stagger: .05,
            onComplete: () => {
                fetch(basicAjax.ajaxurl, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8' },
                    body: new URLSearchParams({
                        action: this.getActionName(),
                        // action: this.contentType === 'product' ? 'filter_products' : 'filter_posts',
                        // action: 'filter_posts',
                        categories: selectedCategories.join(','),
                        search: searchTerm,
                        paged: page,
                    }),
                })
                    .then((response) => response.json())
                    .then((data) => {
                        this.hideLoader();

                        if( this.grid && data.data.content ) {
                            this.grid.innerHTML = data.data.content;

                            new WishAjax();

                            gsap.from(this.grid.children, {
                                opacity: 0,
                                duration: .5,
                                stagger: .1,
                                onComplete: () => {
                                    this.hideLoader();
                                },
                            });
                        } else {
                            this.showError('Aucun contenu trouvé.');
                        }
                        if( data.data.max_num_pages > 1 ) {
                            this.updatePagination(data.data.current_page, data.data.max_num_pages);
                        } else if( this.paginationContainer ) {
                            this.paginationContainer.innerHTML = '';
                        }
                    })
                    .catch((error) => {
                        this.hideLoader();
                        this.grid.classList.remove('loading');
                        this.showError('Erreur de chargement des articles. Veuillez réessayer.');
                        console.error('Error fetching posts:', error);
                    });

            },
        });
    }

    getActionName() {
        const actionMap = {
            'post': 'filter_posts',
            'product': 'filter_products',
            'incontournables': 'filter_incontournables'
        };
        return actionMap[this.contentType] || 'filter_posts';
    }

    applyInitialFilter(categorySlug) {
        const parentCheckboxes = this.filterForm.querySelectorAll(`.category-checkbox[data-parent="${categorySlug}"]`);
        if (parentCheckboxes.length > 0) {
            parentCheckboxes.forEach((checkbox) => {
                checkbox.checked = true;
            });

            const childCategories = Array.from(parentCheckboxes).map((checkbox) => checkbox.value);
            this.updateSelectedFilters(childCategories);
            this.applyFilters();
        } else {
            const checkbox = this.filterForm.querySelector(`.category-checkbox[value="${categorySlug}"]`);
            if (checkbox) {
                checkbox.checked = true;
                this.applyFilters();
            }
        }
    }


    updatePagination(currentPage, maxNumPages) {
        if (!this.paginationContainer) {
            console.warn('Pagination container not found.');
            return;
        }

        if (maxNumPages <= 1) {
            this.paginationContainer.innerHTML = '';
            return;
        }

        this.paginationContainer.innerHTML = '';

        const pageNumbersContainer = document.createElement('div');
        pageNumbersContainer.classList.add('page-numbers');

        const prevButton = document.createElement('button');
        prevButton.classList.add('prev', 'page-numbers');
        prevButton.setAttribute('aria-label', 'Page précédente');
        prevButton.innerHTML = `
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
                <path d="M7.82843 10.9999H20V12.9999H7.82843L13.1924 18.3638L11.7782 19.778L4 11.9999L11.7782 4.22168L13.1924 5.63589L7.82843 10.9999Z"></path>
            </svg>
        `;
        prevButton.disabled = currentPage === 1;
        prevButton.addEventListener('click', () => this.fetchPosts(currentPage - 1));
        pageNumbersContainer.appendChild(prevButton);

        for (let i = 1; i <= maxNumPages; i++) {
            const pageButton = document.createElement('button');
            pageButton.textContent = i;
            pageButton.dataset.page = i;
            pageButton.classList.add('page-numbers');
            pageButton.setAttribute('aria-label', `Page ${i}`);
            pageButton.setAttribute('tabindex', '0');
            if (i === currentPage) {
                pageButton.classList.add('current');
                pageButton.setAttribute('aria-current', 'true');
            }
            pageButton.addEventListener('click', () => this.fetchPosts(i));
            pageNumbersContainer.appendChild(pageButton);
        }

        const nextButton = document.createElement('button');
        nextButton.classList.add('next', 'page-numbers');
        nextButton.setAttribute('aria-label', 'Page suivante');
        nextButton.innerHTML = `
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
                <path d="M16.1716 10.9999L10.8076 5.63589L12.2218 4.22168L20 11.9999L12.2218 19.778L10.8076 18.3638L16.1716 12.9999H4V10.9999H16.1716Z"></path>
            </svg>
        `;
        nextButton.disabled = currentPage === maxNumPages;
        nextButton.addEventListener('click', () => this.fetchPosts(currentPage + 1));
        pageNumbersContainer.appendChild(nextButton);

        this.paginationContainer.appendChild(pageNumbersContainer);
    }

}

